/* ============================================================
   Wido Ops — Missing clock records (HR, perm "attendance.override").
   A worklist of clock fix-ups for the month: incomplete punches
   (clocked in but not out, or vice-versa) and approved hourly-leave
   whose actual out/back clocks aren't matched yet. HR resolves each
   inline — a missing punch via a manual override, an hourly-leave via
   the actual-clock setter (which makes the time-away finally deduct).
   Data: GET /attendance/missing-cases. Exports window.MissingClockScreen.
   ============================================================ */
(function () {
  const { useState, useEffect } = React;
  const D = window.WidoData;
  const PersonCell = window.HRPersonCell;
  // The shared cell shows "title · team"; when a person has no title, fall back to
  // the team alone (avoids rendering a literal "null").
  function personSub(p) {
    return p.title ? undefined : window.rawText(D.helpers.teamName(p.team) || "—");
  }
  const MN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const KIND = {
    missing_in: { label: "Missing clock-in", icon: "login", color: "var(--md-error)" },
    missing_out: { label: "Missing clock-out", icon: "logout", color: "var(--md-error)" },
    hourly: { label: "Hourly leave — match clocks", icon: "directions_walk", color: "#D62C8E" },
  };

  // "2026-06-24" -> "Jun 24"
  function dayLabel(iso) {
    const p = (iso || "").split("-");
    return p.length === 3 ? MN[(+p[1] || 1) - 1] + " " + p[2] : iso;
  }

  // 24-hour time field: hour 00–23 + minute 00–59 selects (full-minute precision so
  // it can show any device-punch time). Value/onChange are "HH:MM". Native
  // <input type=time> renders 12-hour AM/PM under some locales; this stays 24-hour.
  function Time24Field({ value, onChange }) {
    const pad = (n) => String(n).padStart(2, "0");
    const parts = (value || "").split(":");
    const h = value ? pad(parts[0]) : "", m = value ? pad(parts[1] || "0") : "";
    const sel = { className: "input mono", style: { flex: "0 0 auto", width: "auto", minWidth: 68 } };
    const opt = (v) => React.createElement("option", { key: v || "_", value: v }, v || "––");
    return React.createElement("div", { className: "row", style: { gap: 6, alignItems: "center" } },
      React.createElement("select", Object.assign({}, sel, { value: h, onChange: (e) => onChange(pad(e.target.value) + ":" + (m || "00")) }),
        [opt("")].concat(Array.from({ length: 24 }, (_, i) => opt(pad(i))))),
      React.createElement("span", { className: "mono", style: { color: "var(--md-on-surface-variant)" } }, ":"),
      React.createElement("select", Object.assign({}, sel, { value: m, onChange: (e) => onChange((h || "00") + ":" + pad(e.target.value)) }),
        [opt("")].concat(Array.from({ length: 60 }, (_, i) => opt(pad(i))))));
  }

  function ResolveDialog({ c, onClose, onSaved, onToast }) {
    const isHourly = c.kind === "hourly";
    const [v1, setV1] = useState(isHourly ? (c.actualOut || "") : (c.expected || (c.kind === "missing_out" ? "17:30" : "08:30")));
    const [v2, setV2] = useState(isHourly ? (c.actualBack || "") : "");
    const [reason, setReason] = useState("");
    const [recs, setRecs] = useState(null);        // null = not loaded yet; [] = loaded-empty
    const [loadingRecs, setLoadingRecs] = useState(false);
    const [busy, setBusy] = useState(false);
    const valid = isHourly ? (v1 && v2) : (v1 && reason.trim());

    async function loadRecs() {
      setLoadingRecs(true);
      try { const rows = await window.WidoApi.dayPunches(c.person.id, c.date); setRecs(rows || []); }
      catch (e) { setRecs([]); }
      finally { setLoadingRecs(false); }
    }
    // Auto-load the day's punches for an hourly case — matching them IS the job.
    useEffect(() => { if (isHourly) loadRecs(); }, []);

    async function save() {
      if (!valid || busy) return;
      setBusy(true);
      try {
        if (isHourly) {
          await window.WidoApi.setWorkClocks(c.reqId, { actualOut: v1, actualBack: v2 });
        } else {
          const payload = { personId: c.person.id, date: c.date, reason: reason,
            fields: c.kind === "missing_out" ? "out" : "in" };
          if (c.kind === "missing_out") payload.checkOut = v1; else payload.checkIn = v1;
          await window.WidoApi.override(payload);
        }
        onToast && onToast(window.t("Resolved") + " · " + c.person.name);
        onSaved && onSaved();
      } catch (e) {
        let msg = ""; try { const j = JSON.parse(e && e.detail || "{}"); msg = j.detail || j.error || ""; } catch (_) {}
        onToast && onToast((typeof msg === "string" && msg) || window.t("Couldn't save"));
        setBusy(false);
      }
    }

    // A device-punch row: the time + two one-tap assign buttons (highlighted when used).
    function punchRow(r) {
      return React.createElement("div", { key: r.id, className: "between", style: { alignItems: "center", gap: 8, padding: "2px 0" } },
        React.createElement("span", { className: "mono", style: { fontSize: 14, display: "inline-flex", alignItems: "center", gap: 6 } },
          React.createElement("span", { className: "material-symbols-rounded", style: { fontSize: 18, color: "var(--md-on-surface-variant)" } }, "schedule"), r.time),
        React.createElement("div", { className: "row", style: { gap: 6 } },
          React.createElement("button", { className: "btn btn--sm " + (v1 === r.time ? "btn--filled" : "btn--tonal"), onClick: () => setV1(r.time) },
            React.createElement("span", { className: "material-symbols-rounded" }, "logout"), window.t("Out")),
          React.createElement("button", { className: "btn btn--sm " + (v2 === r.time ? "btn--filled" : "btn--tonal"), onClick: () => setV2(r.time) },
            React.createElement("span", { className: "material-symbols-rounded" }, "login"), window.t("In"))));
    }

    return React.createElement("div", { className: "scrim-bg", onClick: onClose },
      React.createElement("div", { className: "sheet", style: { maxWidth: 460 }, onClick: (e) => e.stopPropagation() },
        React.createElement("div", { className: "sheet__head" },
          React.createElement("div", null,
            React.createElement("div", { className: "eyebrow" }, window.t(KIND[c.kind].label)),
            React.createElement("h3", { className: "section-title", style: { fontSize: 20, marginTop: 4 } }, c.person.name + " · " + dayLabel(c.date))),
          React.createElement("button", { className: "icon-btn", onClick: onClose }, React.createElement("span", { className: "material-symbols-rounded" }, "close"))),
        React.createElement("div", { className: "sheet__body" },
          isHourly && React.createElement("div", { className: "banner", style: { background: "var(--md-surface-container)", color: "var(--md-on-surface-variant)" } },
            React.createElement("span", { className: "material-symbols-rounded" }, "schedule"),
            React.createElement("span", { style: { fontSize: 13, lineHeight: 1.5 } }, window.t("Estimated") + ": " + c.expected + ". " + window.t("Tap a punch’s Out / Back below to match the actual times."))),
          // Device records — each punch assignable directly to out or back (hourly only).
          isHourly && React.createElement("div", { className: "field" },
            React.createElement("div", { className: "between", style: { alignItems: "center" } },
              React.createElement("label", { className: "field__label", style: { margin: 0 } }, window.t("Device records")),
              React.createElement("button", { className: "btn btn--text btn--sm", onClick: loadRecs, disabled: loadingRecs },
                React.createElement("span", { className: "material-symbols-rounded" }, "sync"), window.t("Reload"))),
            (recs == null || loadingRecs)
              ? React.createElement("p", { className: "muted", style: { fontSize: 13, margin: "6px 0 0" } }, window.t("Loading…"))
              : recs.length === 0
                ? React.createElement("p", { className: "muted", style: { fontSize: 13, margin: "6px 0 0" } }, window.t("No device records for this day — enter manually."))
                : React.createElement("div", { className: "stack-v", style: { gap: 4, marginTop: 6 } }, recs.map(punchRow))),
          // Resulting times — 24-hour, editable for a manual tweak.
          React.createElement("div", { style: { display: "grid", gridTemplateColumns: isHourly ? "1fr 1fr" : "1fr", gap: 14 } },
            React.createElement("div", { className: "field" },
              React.createElement("label", { className: "field__label" }, isHourly ? window.t("Actual clock-out") : (c.kind === "missing_out" ? window.t("Clock-out") : window.t("Clock-in"))),
              React.createElement(Time24Field, { value: v1, onChange: setV1 })),
            isHourly && React.createElement("div", { className: "field" },
              React.createElement("label", { className: "field__label" }, window.t("Actual clock-back")),
              React.createElement(Time24Field, { value: v2, onChange: setV2 }))),
          !isHourly && React.createElement("div", { className: "field" },
            React.createElement("label", { className: "field__label" }, window.t("Reason") + " *"),
            React.createElement("input", { className: "input", value: reason, onChange: (e) => setReason(e.target.value), placeholder: window.t("e.g. door reader offline, forgot to badge…") }))),
        React.createElement("div", { className: "sheet__foot" },
          React.createElement("button", { className: "btn btn--text", onClick: onClose }, window.t("Cancel")),
          React.createElement("button", { className: "btn btn--filled", disabled: !valid || busy, onClick: save },
            React.createElement("span", { className: "material-symbols-rounded" }, busy ? "hourglass_top" : "check"), window.t("Save entry")))));
  }

  function MissingClockScreen({ settings, onToast, onCount }) {
    // The app's live month (published by bootstrap.js from the backend clock),
    // not the browser's — it's the month the nav badge counts, so opening on it
    // keeps the badge and the list telling the same story.
    const CUR_Y = D.CUR_YEAR || new Date().getFullYear();
    const CUR_M = D.CUR_MONTH || (new Date().getMonth() + 1);
    const [year, setYear] = useState(CUR_Y);
    const [month, setMonth] = useState(CUR_M);
    const [kind, setKind] = useState("all");
    const [q, setQ] = useState("");
    const [data, setData] = useState({ cases: [], counts: { total: 0, missing: 0, hourly: 0 } });
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    const EMPTY = { total: 0, missing: 0, hourly: 0 };
    function load() {
      setLoading(true);
      window.WidoApi.missingCases(year, month)
        .then((res) => {
          const counts = (res && res.counts) || EMPTY;
          setData({ cases: (res && res.cases) || [], counts: counts });
          setLoading(false);
          // Keep the nav badge honest as cases are resolved — but only from the
          // month it counts. Browsing back to June must not relabel it.
          if (onCount && year === CUR_Y && month === CUR_M) onCount(counts.total);
        })
        .catch(() => { setData({ cases: [], counts: EMPTY }); setLoading(false); onToast && onToast(window.t("Couldn't load missing cases")); });
    }
    useEffect(() => { load(); }, [year, month]);

    let cases = data.cases;
    if (kind !== "all") cases = cases.filter((c) => kind === "missing" ? c.kind !== "hourly" : c.kind === "hourly");
    if (q) cases = cases.filter((c) => (c.person.name || "").toLowerCase().includes(q.toLowerCase()));
    const counts = data.counts;
    const YEARS = [year - 1, year];

    return React.createElement("div", { className: "stack-v", style: { gap: 20 } },
      React.createElement("div", { className: "between wrap" },
        React.createElement("div", null,
          React.createElement("h2", { className: "section-title" }, window.t("Missing clock records")),
          React.createElement("p", { className: "muted", style: { margin: "4px 0 0" } }, window.t("Days needing a clock fix or actual hourly-leave times."))),
        React.createElement("div", { className: "row", style: { gap: 8, flexWrap: "wrap" } },
          React.createElement("select", { className: "select", style: { height: 40 }, value: month, onChange: (e) => setMonth(+e.target.value) },
            MN.map((mn, i) => React.createElement("option", { key: i, value: i + 1 }, window.t(mn)))),
          React.createElement("select", { className: "select", style: { height: 40 }, value: year, onChange: (e) => setYear(+e.target.value) },
            YEARS.map((y) => React.createElement("option", { key: y, value: y }, y))))),

      React.createElement("div", { className: "row", style: { gap: 10, flexWrap: "wrap" } },
        React.createElement("div", { className: "seg" },
          React.createElement("button", { className: "seg__btn" + (kind === "all" ? " is-on" : ""), onClick: () => setKind("all") }, window.t("All") + " (" + counts.total + ")"),
          React.createElement("button", { className: "seg__btn" + (kind === "missing" ? " is-on" : ""), onClick: () => setKind("missing") }, window.t("Missing punches") + " (" + counts.missing + ")"),
          React.createElement("button", { className: "seg__btn" + (kind === "hourly" ? " is-on" : ""), onClick: () => setKind("hourly") }, window.t("Hourly leave") + " (" + counts.hourly + ")")),
        React.createElement("input", { className: "input", style: { height: 40, width: 200 }, placeholder: window.t("Search name…"), value: q, onChange: (e) => setQ(e.target.value) })),

      loading
        ? React.createElement("div", { className: "card empty" }, React.createElement("span", { className: "material-symbols-rounded" }, "hourglass_top"), React.createElement("p", null, window.t("Loading…")))
        : cases.length === 0
          ? React.createElement("div", { className: "card empty" }, React.createElement("span", { className: "material-symbols-rounded" }, "task_alt"), React.createElement("p", null, window.t("Nothing to fix — all clock records are complete.")))
          : React.createElement("div", { className: "card tablewrap" },
            React.createElement("table", { className: "data" },
              React.createElement("thead", null, React.createElement("tr", null,
                [window.t("Employee"), window.t("Date"), window.t("Issue"), window.t("Expected"), window.t("Recorded"), ""].map((h, i) => React.createElement("th", { key: i, style: i === 5 ? { textAlign: "right" } : null }, h)))),
              React.createElement("tbody", null, cases.map((c) => {
                const k = KIND[c.kind];
                return React.createElement("tr", { key: c.id },
                  React.createElement("td", null, React.createElement(PersonCell, { person: c.person, sub: personSub(c.person) })),
                  React.createElement("td", { className: "mono", style: { whiteSpace: "nowrap" } }, dayLabel(c.date)),
                  React.createElement("td", null, React.createElement("span", { className: "tag", style: { background: k.color + "22", color: k.color } },
                    React.createElement("span", { className: "material-symbols-rounded" }, k.icon), window.t(k.label))),
                  React.createElement("td", { className: "muted mono", style: { fontSize: 12.5 } }, c.expected),
                  React.createElement("td", { className: "mono", style: { fontSize: 12.5, color: c.recorded ? "var(--md-on-surface)" : "var(--md-outline)" } }, c.recorded || "—"),
                  React.createElement("td", { style: { textAlign: "right" } }, React.createElement("button", { className: "btn btn--filled btn--sm", onClick: () => setEditing(c) },
                    React.createElement("span", { className: "material-symbols-rounded" }, "edit"), window.t("Resolve"))));
              })))),

      editing && React.createElement(ResolveDialog, { c: editing, onClose: () => setEditing(null), onToast: onToast, onSaved: () => { setEditing(null); load(); } }));
  }

  window.MissingClockScreen = MissingClockScreen;
})();
